import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { HelpCircle, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAddItem,
  useAddSection,
  useCreateTemplate,
  useDeleteItem,
  useDeleteSection,
  useTemplate,
  useUpdateItem,
  useUpdateSection,
  useUpdateTemplate,
} from "@/hooks/use-templates";
import { useSession } from "@/lib/auth-client";
import { TEMPLATE_STATUS_BADGE_VARIANTS, TEMPLATE_STATUS_LABELS } from "@/lib/template-status";

interface TemplateFormProps {
  templateId?: string;
}

const createSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  projectType: z.string().min(1, "Project type is required"),
});

type CreateValues = z.infer<typeof createSchema>;

export function TemplateForm({ templateId }: TemplateFormProps) {
  const navigate = useNavigate();
  const isEdit = !!templateId;

  const { data: template, isLoading, isError, refetch } = useTemplate(templateId);
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate(templateId ?? "");
  const addSection = useAddSection(templateId ?? "");
  const updateSection = useUpdateSection(templateId ?? "");
  const deleteSection = useDeleteSection(templateId ?? "");

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", description: "", projectType: "" },
  });

  // Populate form when template loads in edit mode
  useEffect(() => {
    if (template && isEdit) {
      form.reset({
        name: template.name,
        description: template.description ?? "",
        projectType: template.projectType,
      });
    }
  }, [template, isEdit, form]);

  async function onCreateSubmit(values: CreateValues) {
    const record = await createTemplate.mutateAsync({
      name: values.name,
      description: values.description || undefined,
      projectType: values.projectType,
    });
    navigate({ to: "/dashboard/templates/$templateId", params: { templateId: record.id } });
  }

  async function handleUpdateField(field: "name" | "description" | "projectType", value: string) {
    if (!templateId) return;
    await updateTemplate.mutateAsync({ [field]: value });
  }

  async function handleStatusChange(status: string) {
    if (!templateId) return;
    await updateTemplate.mutateAsync({ status });
  }

  async function handleAddSection() {
    if (!templateId) return;
    await addSection.mutateAsync({ title: "New Section" });
  }

  if (isEdit && isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isEdit && isError) {
    return <ErrorState message="Failed to load template" onRetry={() => refetch()} />;
  }

  // Create mode
  if (!isEdit) {
    return (
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onCreateSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Security Audit Template" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="projectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Project Type</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Web Application, Infrastructure" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Brief description of this template" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={createTemplate.isPending}>
                  {createTemplate.isPending ? "Creating…" : "Create Template"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate({ to: "/dashboard/templates" })}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    );
  }

  // Edit mode
  if (!template) return null;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header fields */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Template Details</CardTitle>
          <Select
            value={template.status}
            onValueChange={handleStatusChange}
            disabled={updateTemplate.isPending}
          >
            <SelectTrigger size="sm" className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Badge variant={TEMPLATE_STATUS_BADGE_VARIANTS[template.status] ?? "secondary"}>
              {TEMPLATE_STATUS_LABELS[template.status] ?? template.status}
            </Badge>
            {template.status === "inactive" && (
              <span className="text-xs text-muted-foreground">
                Hidden from new audits — existing audits using this template are unaffected.
              </span>
            )}
          </div>
          <EditableField
            label="Name"
            value={template.name}
            onSave={(v) => handleUpdateField("name", v)}
          />
          <EditableField
            label="Project Type"
            value={template.projectType}
            onSave={(v) => handleUpdateField("projectType", v)}
          />
          <EditableField
            label="Description"
            value={template.description ?? ""}
            onSave={(v) => handleUpdateField("description", v)}
          />
        </CardContent>
      </Card>

      {/* Sections */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sections</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddSection}
            disabled={addSection.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Section
          </Button>
        </div>

        {template.sections.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No sections yet. Add a section to get started.
          </p>
        )}

        {template.sections.map((section) => (
          <SectionEditor
            key={section.id}
            section={section}
            templateId={templateId}
            onUpdateSection={(title) => updateSection.mutateAsync({ sectionId: section.id, title })}
            onDeleteSection={() => deleteSection.mutateAsync(section.id)}
          />
        ))}
      </div>
    </div>
  );
}

interface EditableFieldProps {
  label: string;
  value: string;
  onSave: (value: string) => Promise<void>;
}

function EditableField({ label, value, onSave }: EditableFieldProps) {
  const [editing, setEditing] = useState(false);
  const [current, setCurrent] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrent(value);
  }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  async function handleBlur() {
    setEditing(false);
    if (current !== value) {
      await onSave(current);
    }
  }

  const fieldId = `editable-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="space-y-1">
      <label htmlFor={fieldId} className="text-sm font-medium">
        {label}
      </label>
      {editing ? (
        <Input
          id={fieldId}
          ref={inputRef}
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === "Enter") inputRef.current?.blur();
            if (e.key === "Escape") {
              setCurrent(value);
              setEditing(false);
            }
          }}
        />
      ) : (
        <div
          className="px-3 py-2 rounded-md border border-transparent hover:border-border cursor-text text-sm min-h-9 flex items-center"
          onClick={() => setEditing(true)}
        >
          {current || <span className="text-muted-foreground">Click to edit</span>}
        </div>
      )}
    </div>
  );
}

interface SectionEditorProps {
  section: {
    id: string;
    title: string;
    order: number;
    items: Array<{
      id: string;
      text: string;
      optional: boolean;
      upload: boolean;
      helpText: string | null;
      order: number;
    }>;
  };
  templateId: string;
  onUpdateSection: (title: string) => Promise<void>;
  onDeleteSection: () => Promise<void>;
}

function SectionEditor({
  section,
  templateId,
  onUpdateSection,
  onDeleteSection,
}: SectionEditorProps) {
  const [title, setTitle] = useState(section.title);
  const addItem = useAddItem(templateId, section.id);

  useEffect(() => {
    setTitle(section.title);
  }, [section.title]);

  async function handleTitleBlur() {
    if (title !== section.title && title.trim()) {
      await onUpdateSection(title);
    }
  }

  async function handleAddItem() {
    await addItem.mutateAsync({ text: "New item" });
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            className="font-semibold text-base flex-1"
            placeholder="Section title"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={onDeleteSection}
            className="shrink-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {section.items.map((item) => (
          <ItemEditor key={item.id} item={item} templateId={templateId} sectionId={section.id} />
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAddItem}
          disabled={addItem.isPending}
          className="w-full text-muted-foreground"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </CardContent>
    </Card>
  );
}

interface ItemEditorProps {
  item: {
    id: string;
    text: string;
    optional: boolean;
    upload: boolean;
    helpText: string | null;
    order: number;
  };
  templateId: string;
  sectionId: string;
}

function ItemEditor({ item, templateId, sectionId }: ItemEditorProps) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";
  const [text, setText] = useState(item.text);
  const [helpTextOpen, setHelpTextOpen] = useState(false);
  const [helpText, setHelpText] = useState(item.helpText ?? "");
  const updateItem = useUpdateItem(templateId, sectionId);
  const deleteItem = useDeleteItem(templateId, sectionId);

  useEffect(() => {
    setText(item.text);
  }, [item.text]);

  useEffect(() => {
    setHelpText(item.helpText ?? "");
  }, [item.helpText]);

  async function handleTextBlur() {
    if (text !== item.text && text.trim()) {
      await updateItem.mutateAsync({ itemId: item.id, text });
    }
  }

  async function handleToggleOptional() {
    await updateItem.mutateAsync({ itemId: item.id, optional: !item.optional });
  }

  async function handleToggleUpload() {
    await updateItem.mutateAsync({ itemId: item.id, upload: !item.upload });
  }

  async function handleHelpTextBlur() {
    const trimmed = helpText.trim();
    if (trimmed !== (item.helpText ?? "")) {
      await updateItem.mutateAsync({ itemId: item.id, helpText: trimmed || null });
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 rounded border border-muted-foreground shrink-0" />
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleTextBlur}
          className="flex-1 h-8 text-sm"
          placeholder="Item text"
        />
        {isAdmin && (
          <button
            type="button"
            onClick={() => setHelpTextOpen((v) => !v)}
            className="shrink-0"
            title={item.helpText ? "Edit help text" : "Add help text"}
          >
            <Badge
              variant="outline"
              className={`text-xs cursor-pointer gap-1 ${
                item.helpText
                  ? "border-purple-400 text-purple-600 dark:text-purple-400"
                  : "border-transparent bg-muted text-muted-foreground"
              }`}
            >
              <HelpCircle className="h-3 w-3" />
              Help text
            </Badge>
          </button>
        )}
        <button
          type="button"
          onClick={handleToggleUpload}
          className="shrink-0"
          title={item.upload ? "Remove upload button" : "Add upload button"}
        >
          {item.upload ? (
            <Badge
              variant="outline"
              className="text-xs cursor-pointer gap-1 border-blue-400 text-blue-600 dark:text-blue-400"
            >
              <Upload className="h-3 w-3" />
              Upload
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-xs cursor-pointer border-transparent bg-muted text-muted-foreground"
            >
              No upload
            </Badge>
          )}
        </button>
        <button
          type="button"
          onClick={handleToggleOptional}
          className="shrink-0"
          title={item.optional ? "Mark as required" : "Mark as optional"}
        >
          {item.optional ? (
            <Badge
              variant="outline"
              className="text-xs cursor-pointer border-transparent bg-muted text-muted-foreground"
            >
              Optional
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-xs cursor-pointer border-amber-400 text-amber-600 dark:text-amber-400"
            >
              Required
            </Badge>
          )}
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
          onClick={() => deleteItem.mutateAsync(item.id)}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      {isAdmin && helpTextOpen && (
        <div className="pl-6">
          <Textarea
            value={helpText}
            onChange={(e) => setHelpText(e.target.value)}
            onBlur={handleHelpTextBlur}
            placeholder="Help text shown to auditors when they hover the help icon…"
            rows={2}
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
}
